import { FaCalendarCheck, FaChartLine, FaUserShield } from "react-icons/fa";
import { FiUser } from "react-icons/fi";
import type { TFunction } from "i18next";
import type { DashboardPersonalLink, DashboardUpcomingItem } from "../types/dashboard.types";
import StatCard from "./StatCard";
import DashboardUpcomingPanel from "./DashboardUpcomingPanel";

interface DashboardPersonalOverviewProps {
  authenticated: boolean;
  canSeeMyEnvironment: boolean;
  displayName: string;
  todayLabel: string;
  openActionsTotal: number;
  personalLinks: DashboardPersonalLink[];
  upcoming: DashboardUpcomingItem[];
  upcomingLoading: boolean;
  selectedUpcoming: DashboardUpcomingItem | null;
  userRoleLabel: string;
  lastLoginLabel: string;
  locale: string;
  t: TFunction;
  onSelectUpcoming: (item: DashboardUpcomingItem | null) => void;
  onNavigate: (path: string) => void;
}

const DashboardPersonalOverview = ({
  authenticated,
  canSeeMyEnvironment,
  displayName,
  todayLabel,
  openActionsTotal,
  personalLinks,
  upcoming,
  upcomingLoading,
  selectedUpcoming,
  userRoleLabel,
  lastLoginLabel,
  locale,
  t,
  onSelectUpcoming,
  onNavigate,
}: DashboardPersonalOverviewProps) => {
  if (!authenticated) return null;

  if (!canSeeMyEnvironment) {
    return (
      <section className="dashboard-enter rounded-[28px] border border-slate-200/80 bg-white/95 p-6 shadow-[0_14px_35px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-950/70 md:p-8">
        <h2 className="text-3xl font-semibold text-slate-900 dark:text-white">
          {t("dashboard.welcome", { defaultValue: "Welcome back" })}, <span className="font-bold">{displayName}</span>!
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          {t("dashboard.your_roles", { defaultValue: "Your access level" })}: {userRoleLabel}
        </p>
        <p className="mt-4 text-base text-slate-700 dark:text-slate-200">
          {t("dashboard.intro", {
            defaultValue: "Here you can quickly see your open actions, manage families, track school information and review reports.",
          })}
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label={t("dashboard.today", { defaultValue: "Today" })} value={todayLabel} />
          <StatCard label={t("dashboard.open_actions", { defaultValue: "Open actions" })} value={openActionsTotal} />
          <StatCard label={t("dashboard.last_login", { defaultValue: "Last login" })} value={lastLoginLabel} />
        </div>
      </section>
    );
  }

  return (
    <section className="dashboard-enter rounded-[28px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_14px_35px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-950/70 md:p-6">
      <div className="grid gap-6 xl:grid-cols-[1.35fr_0.9fr] xl:items-start">
        <div className="min-w-0 xl:pr-2">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">
            {t("dashboard.personal.title", { defaultValue: "For you" })}
          </p>
          <h2 className="mt-2 text-2xl font-semibold leading-tight text-slate-950 dark:text-white md:text-3xl">
            {t("dashboard.welcome", { defaultValue: "Welcome back" })}, <span className="font-bold">{displayName}</span>
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <StatCard icon={<FaCalendarCheck />} label={t("dashboard.personal.today", { defaultValue: "Today" })} value={todayLabel} />
            <StatCard icon={<FaChartLine />} label={t("dashboard.personal.openActions", { defaultValue: "Open actions" })} value={openActionsTotal} />
          </div>
          <div className="mt-6">
            <p className="text-base font-semibold text-slate-950 dark:text-white">
              {t("dashboard.personal.myEnvironment", { defaultValue: "My environment" })}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {personalLinks.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onNavigate(item.path)}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-indigo-100 bg-indigo-50/70 px-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:bg-white hover:text-indigo-900 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:transform-none dark:border-white/10 dark:bg-indigo-950/30 dark:text-indigo-100"
                >
                  <span className="text-xl text-indigo-500 dark:text-indigo-200">{item.icon}</span>
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DashboardUpcomingPanel
          items={upcoming}
          loading={upcomingLoading}
          selected={selectedUpcoming}
          locale={locale}
          agendaActivitiesLabel={t("dashboard.myEnvironment.agendaActivities", { defaultValue: "Agenda & activities" })}
          t={t}
          onSelect={onSelectUpcoming}
          onNavigate={onNavigate}
        />
      </div>
      <div className="mt-6 border-t border-slate-200/80 pt-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400 xl:flex xl:items-center xl:justify-between xl:gap-6">
        <p className="flex min-w-0 items-center gap-3">
          <FaUserShield className="shrink-0 text-slate-400" />
          <span>
            <span className="font-bold text-slate-700 dark:text-slate-200">{t("dashboard.personal.roles", { defaultValue: "Your access level" })}:</span>{" "}
            {userRoleLabel}
          </span>
        </p>
        <p className="mt-2 flex min-w-0 items-center gap-3 xl:mt-0">
          <FiUser className="shrink-0 text-slate-400" />
          <span>
            <span className="font-bold text-slate-700 dark:text-slate-200">{t("dashboard.personal.lastLogin", { defaultValue: "Last login" })}:</span>{" "}
            {lastLoginLabel}
          </span>
        </p>
      </div>
    </section>
  );
};

export default DashboardPersonalOverview;
